/**
 * Display formatting for values that arrive as strings.
 *
 * These functions never convert a monetary string to a number for arithmetic.
 * They group digits and trim trailing zeros textually, so the value rendered is
 * exactly the value the backend sent.
 */

export function formatQuantity(value: string | null | undefined, maxDecimals = 8): string {
  if (value === null || value === undefined || value === '') return '—';
  const negative = value.startsWith('-');
  const unsigned = negative ? value.slice(1) : value;
  const [whole = '0', fraction = ''] = unsigned.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const trimmed = fraction.slice(0, maxDecimals).replace(/0+$/, '');
  return `${negative ? '-' : ''}${grouped}${trimmed ? `.${trimmed}` : ''}`;
}

export function formatMoney(value: string | null | undefined, asset = 'USDT'): string {
  if (value === null || value === undefined || value === '') return '—';
  return `${formatQuantity(value, 2)} ${asset}`;
}

/**
 * A price, with as many decimals as its size needs. Two decimals turned an
 * ONDO entry of 0.5351 into 0.53, which is a different price.
 */
export function formatPrice(value: string | null | undefined, asset = 'USDT'): string {
  if (value === null || value === undefined || value === '') return '—';
  const whole = (value.startsWith('-') ? value.slice(1) : value).split('.')[0] ?? '0';
  const decimals = whole === '0' ? 8 : whole.length === 1 ? 4 : 2;
  return `${formatQuantity(value, decimals)} ${asset}`;
}

/** Sign of a decimal string, without parsing it into a float. */
export function signOf(value: string | null | undefined): -1 | 0 | 1 {
  if (!value) return 0;
  if (value.startsWith('-')) return /[1-9]/.test(value) ? -1 : 0;
  return /[1-9]/.test(value) ? 1 : 0;
}

export function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleTimeString([], { hour12: false });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString([], { hour12: false });
}

export function relativeAge(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'unknown';
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
